import React, { useState } from 'react';
import { contactInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Phone, Send, CheckCircle, Scale } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({ name: '', email: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-white py-24 lg:py-32">
      <div className="max-w-[87.5rem] mx-auto px-10">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
          {/* Left Column - Contact Info */}
          <div className="lg:w-1/2">
            {/* Attorney Badge */}
            <div className="inline-flex items-center gap-2 bg-[#3b82f6]/10 border border-[#3b82f6]/30 rounded-full px-4 py-2 mb-6">
              <Scale size={16} className="text-[#3b82f6]" />
              <span className="text-[#3b82f6] text-sm font-semibold uppercase tracking-wide">
                Attorney Inquiries Welcome
              </span>
            </div>
            
            <h2 
              className="text-[#0f172a] font-black uppercase leading-[0.85] mb-8"
              style={{ 
                fontFamily: "'Inter', Arial, sans-serif",
                fontSize: 'clamp(2rem, 3rem + 1vw, 3.5rem)'
              }}
            >
              Let's Work Together
            </h2>
            
            <p className="text-[#475569] text-lg font-medium leading-relaxed mb-12">
              Ready to discuss how I can support your real estate practice? Get in touch today for a consultation. I'm here to help your firm deliver exceptional results.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <a 
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-full bg-[#3b82f6]/10 flex items-center justify-center flex-shrink-0 transition-colors duration-300 group-hover:bg-[#3b82f6]/20">
                  <Mail size={22} className="text-[#3b82f6]" />
                </div>
                <span className="text-[#475569] font-medium group-hover:text-[#0f172a] transition-colors duration-300">
                  {contactInfo.email}
                </span>
              </a>

              <a 
                href={`tel:${contactInfo.phone}`}
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-full bg-[#3b82f6]/10 flex items-center justify-center flex-shrink-0 transition-colors duration-300 group-hover:bg-[#3b82f6]/20">
                  <Phone size={22} className="text-[#3b82f6]" />
                </div>
                <span className="text-[#475569] font-medium group-hover:text-[#0f172a] transition-colors duration-300">
                  {contactInfo.phone}
                </span>
              </a>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="lg:w-1/2">
            <div className="bg-[#f8fafc] border border-[#e2e8f0] p-8 lg:p-10 rounded-lg">
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-20 h-20 rounded-full bg-[#3b82f6]/10 flex items-center justify-center mb-6">
                    <CheckCircle size={40} className="text-[#3b82f6]" />
                  </div>
                  <h3 className="text-[#0f172a] text-2xl font-bold mb-3">Message Sent!</h3>
                  <p className="text-[#64748b]">We'll get back to you as soon as possible.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#0f172a] font-medium">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="bg-white border-[#e2e8f0] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#3b82f6] focus:ring-[#3b82f6] h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#0f172a] font-medium">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@lawfirm.com"
                      className="bg-white border-[#e2e8f0] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#3b82f6] focus:ring-[#3b82f6] h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#0f172a] font-medium">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your paralegal support needs..."
                      rows={5}
                      className="bg-white border-[#e2e8f0] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#3b82f6] focus:ring-[#3b82f6] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#3b82f6] text-white border-none rounded-[10rem] px-8 py-4 h-14 font-semibold text-base cursor-pointer transition-all duration-300 hover:bg-[#2563eb] hover:scale-[1.02] active:scale-[0.98] uppercase tracking-tight inline-flex items-center justify-center gap-3"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Send Message
                        <Send size={18} />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
